import { useParams, useNavigate } from 'react-router-dom';
import { useHomeworkProgression } from '../api/homework';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine } from 'recharts';

export function HomeworkProgressionPage() {
  const { assignmentId } = useParams<{ assignmentId: string }>();
  const navigate = useNavigate();
  const { data, isLoading, error } = useHomeworkProgression(assignmentId);

  if (isLoading) {
    return (
      <div className="container mx-auto p-6">
        <div className="flex items-center justify-center h-64">
          <div className="text-gray-500">Loading progression data...</div>
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="container mx-auto p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <h3 className="text-red-800 font-semibold">Error loading progression</h3>
          <p className="text-red-600 text-sm mt-1">
            {error instanceof Error ? error.message : 'Failed to load homework progression'}
          </p>
          <button
            onClick={() => navigate('/')}
            className="mt-3 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
          >
            Back to Home
          </button>
        </div>
      </div>
    );
  }

  // Determine whether to show date or time on x-axis based on data range
  const timestamps = data.progression.map(p => new Date(p.timestamp).getTime());
  const timeRangeMs = timestamps.length > 1
    ? Math.max(...timestamps) - Math.min(...timestamps)
    : 0;
  const showTimeOnly = timeRangeMs < 24 * 60 * 60 * 1000; // under 24 hours

  // Format data for the chart
  const allChartData = data.progression.map(point => ({
    timestamp: new Date(point.timestamp).getTime(),
    percentage: point.completionPercentage,
    formattedTime: new Date(point.timestamp).toLocaleString('en-GB', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }),
  }));

  // Strip out the initial 0% phase - start from midnight on the day work began
  const firstNonZeroIndex = allChartData.findIndex(point => point.percentage > 0);
  let chartData = allChartData;

  if (firstNonZeroIndex > 0) {
    // Get midnight of the day they started working
    const startDate = new Date(allChartData[firstNonZeroIndex]!.timestamp);
    const midnightStart = new Date(startDate);
    midnightStart.setHours(0, 0, 0, 0);
    const midnightTimestamp = midnightStart.getTime();

    // Include all data points from midnight onwards
    const startIndex = allChartData.findIndex(point => point.timestamp >= midnightTimestamp);
    chartData = startIndex >= 0 ? allChartData.slice(startIndex) : allChartData;
  }

  const dueDate = new Date(data.dueDate);
  const currentPercentage = data.progression[data.progression.length - 1]?.completionPercentage ?? 0;
  const isComplete = currentPercentage === 100;

  // Color based on subject
  const subjectColors: Record<string, string> = {
    maths: '#3b82f6',    // blue
    science: '#10b981',  // green
    reader: '#a855f7',   // purple
  };
  const lineColor = subjectColors[data.subject] || '#3b82f6';

  return (
    <div className="container mx-auto p-6">
      {/* Header with back button */}
      <div className="mb-4 flex items-center gap-4">
        <button
          onClick={() => navigate('/')}
          className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
          title="Back to homework"
        >
          <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            {data.childName}'s {data.subject.charAt(0).toUpperCase() + data.subject.slice(1)}
          </h1>
        </div>
      </div>

      {/* Navigation banner with due date */}
      <div className="bg-white border border-gray-200 rounded-lg p-4 mb-4">
        <div className="flex items-center justify-between">
          <button
            onClick={() => data.previousAssignmentId && navigate(`/homework/${data.previousAssignmentId}`)}
            disabled={!data.previousAssignmentId}
            className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors disabled:text-gray-300 disabled:hover:bg-transparent disabled:cursor-not-allowed"
            title="Previous homework"
          >
            <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <div className="text-center">
            <p className="text-lg font-semibold text-gray-900">
              Due: {dueDate.toLocaleString('en-GB', {
                weekday: 'short',
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
              })}
            </p>
          </div>
          <button
            onClick={() => data.nextAssignmentId && navigate(`/homework/${data.nextAssignmentId}`)}
            disabled={!data.nextAssignmentId}
            className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors disabled:text-gray-300 disabled:hover:bg-transparent disabled:cursor-not-allowed"
            title="Next homework"
          >
            <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>
      </div>

      {/* Current status card */}
      <div className="bg-white border border-gray-200 rounded-lg p-6 mb-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-gray-600">Current Progress</p>
            <p className="text-4xl font-bold text-gray-900 mt-1">
              {currentPercentage}%
            </p>
          </div>
          {isComplete && (
            <div className="flex items-center gap-2 text-green-600">
              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
              </svg>
              <span className="text-lg font-semibold">Complete!</span>
            </div>
          )}
        </div>
      </div>

      {/* Chart */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Progress Over Time</h2>
        <ResponsiveContainer width="100%" height={400}>
          <LineChart data={chartData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis
              dataKey="timestamp"
              type="number"
              domain={['dataMin', 'dataMax']}
              tickFormatter={(value) => {
                return new Date(value).toLocaleString('en-GB', showTimeOnly
                  ? { hour: '2-digit', minute: '2-digit' }
                  : { month: 'short', day: 'numeric' }
                );
              }}
            />
            <YAxis domain={[0, 100]} tickFormatter={(value) => `${value}%`} />
            <Tooltip
              content={({ active, payload }) => {
                if (active && payload && payload.length) {
                  const data = payload[0].payload;
                  return (
                    <div className="bg-white border border-gray-300 rounded-lg p-3 shadow-lg">
                      <p className="text-sm font-semibold text-gray-900">
                        {data.percentage}%
                      </p>
                      <p className="text-xs text-gray-600 mt-1">
                        {data.formattedTime}
                      </p>
                    </div>
                  );
                }
                return null;
              }}
            />
            <ReferenceLine y={100} stroke="#10b981" strokeDasharray="3 3" />
            <Line
              type="monotone"
              dataKey="percentage"
              stroke={lineColor}
              strokeWidth={3}
              dot={false}
              activeDot={{ r: 6 }}
            />
          </LineChart>
        </ResponsiveContainer>

        {/* Summary stats */}
        <div className="grid grid-cols-2 gap-4 mt-6">
          <div className="text-center">
            <p className="text-xs text-gray-600">Started</p>
            <p className="text-sm font-semibold text-gray-900 mt-1">
              {chartData.length > 0 && chartData[0]
                ? new Date(chartData[0].timestamp).toLocaleDateString('en-GB', {
                    month: 'short',
                    day: 'numeric'
                  })
                : 'Not started'}
            </p>
          </div>
          <div className="text-center">
            <p className="text-xs text-gray-600">Status</p>
            <p className="text-sm font-semibold text-gray-900 mt-1">
              {isComplete ? '✅ Complete' : '🔄 In Progress'}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
